import { Link } from "@tanstack/react-router";
import { Clock, Facebook, Mail, MapPin, Phone } from "lucide-react";
import logo from "@/assets/amigo-logo.png";
import { NewsletterForm } from "./NewsletterForm";
import {
  BUSINESS,
  DESIGNER_URL,
  FACEBOOK_URL,
  MAPS_URL,
  NAV_LINKS,
  routeTo,
  TOUR_360_URL,
} from "@/lib/site";

type SiteFooterProps = {
  newsletterHeading?: string;
  newsletterDescription?: string;
};

export function SiteFooter({ newsletterHeading, newsletterDescription }: SiteFooterProps) {
  const year = new Date().getFullYear();

  return (
    <footer
      className="bg-forest-deep pb-24 pt-20 text-cream sm:pb-16"
      aria-labelledby="footer-title"
    >
      <h2 id="footer-title" className="sr-only">
        Contact and site information
      </h2>

      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr_1fr] lg:gap-16">
          <div>
            <img
              src={logo}
              alt="Amigo Inn & RV Park"
              width={300}
              height={100}
              loading="lazy"
              className="h-12 w-auto"
            />
            <address className="mt-6 space-y-3 text-sm not-italic text-cream/85">
              <p className="font-display text-lg text-cream">{BUSINESS.name}</p>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline items-start"
              >
                <MapPin className="mt-0.5 size-4 shrink-0 text-sand" aria-hidden="true" />
                <span>
                  {BUSINESS.street}
                  <br />
                  {BUSINESS.city}, {BUSINESS.region} {BUSINESS.postalCode}
                </span>
              </a>
              <a href={BUSINESS.phoneHref} className="link-underline">
                <Phone className="size-4 shrink-0 text-sand" aria-hidden="true" />
                {BUSINESS.phone}
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="link-underline">
                <Mail className="size-4 shrink-0 text-sand" aria-hidden="true" />
                {BUSINESS.email}
              </a>
              <p className="flex items-center gap-2">
                <Clock className="size-4 shrink-0 text-sand" aria-hidden="true" />
                Office Hours: {BUSINESS.officeHours}
              </p>
            </address>

            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Amigo Inn & RV Park on Facebook"
              className="mt-6 inline-flex size-11 items-center justify-center rounded-full border border-cream/25 transition-colors hover:bg-cream/15"
            >
              <Facebook className="size-4" aria-hidden="true" />
            </a>
          </div>

          <nav aria-label="Footer">
            <p className="eyebrow text-sand">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              {NAV_LINKS.map((item) => (
                <li key={item.href}>
                  <Link
                    to={routeTo(item.href)}
                    className="link-underline text-cream/85 hover:text-cream"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                {/* TODO: verify 360° tour URL before production deployment */}
                <a
                  href={TOUR_360_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-cream/85 hover:text-cream"
                >
                  360° Video Tour
                </a>
              </li>
            </ul>
          </nav>

          <NewsletterForm
            {...(newsletterHeading ? { heading: newsletterHeading } : {})}
            {...(newsletterDescription ? { description: newsletterDescription } : {})}
          />
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-cream/15 pt-8 text-xs text-cream/65 sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} Amigo Inn &amp; RV Park. All Rights Reserved.</p>
          <p>
            Website Design by{" "}
            <a
              href={DESIGNER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline text-cream/85"
            >
              SEPI Marketing
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
